import type { Filter, PromptTabs } from "@1js/pl-card-components";
import type { Prompt, Status } from "@1js/pl-types";
import { useMsal } from "@azure/msal-react";
import { useId } from "@fluentui/react-components";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { useShareToast } from "../../hooks/toast/useShareToast";
import { useSwipe } from "../../hooks/useCarousel/useSwipe";
import { useFeatureEnablement } from "../../hooks/useFeatureEnablement";
import { useFlightContext } from "../../hooks/useFlightContext";
import { useSharePrompt } from "../../hooks/useSharePrompt";
import { MetaOSAppContext } from "../../pages/metaos/MetaOSAppContext";
import useTelemetry from "../../utils/Telemetry/useTelemetry";
import type { WebPrompt } from "../data";
import { deserializeFilters, serializeFilters } from "./AllPrompts.web.data";

export type UseAllPromptsProps = {
  status?: Status;
  prompts: Prompt[];
  allPrompts: Prompt[];
  filters: Filter[];
  selectedTab: PromptTabs;
  onFilterChanged: (filters: Record<string, string[]>) => void;
  onPromptClicked: (prompt: Prompt) => void;
  onPromptsTabSelected?: (value: PromptTabs) => void;
  groupPrompts: Prompt[];
};

export const useAllPrompts = ({
  status,
  prompts,
  allPrompts,
  filters,
  selectedTab,
  onFilterChanged,
  onPromptClicked,
  onPromptsTabSelected,
  groupPrompts,
}: UseAllPromptsProps) => {
  const resultsAnnouncementId = useId("prompt-cards-description");
  const isLoading = status === "loading";
  const telemetry = useTelemetry();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [activePrompt, setActivePrompt] = React.useState<WebPrompt>();
  const swipe = useSwipe();
  const promptRefs = React.useRef(
    new Map<string, React.RefObject<HTMLDivElement>>()
  );
  const sharePrompt = useSharePrompt();

  const metaOSAppContext = React.useContext(MetaOSAppContext);
  const msalInstance = useMsal();
  const instance =
    metaOSAppContext.isMosApp && metaOSAppContext.pca
      ? msalInstance.instance
      : undefined;

  const toasterId = useId("toaster");
  const {
    notifyShareCopyLinkSuccess,
    notifyShareEmailLinkSuccess,
    notifyShareCopyLinkFail,
  } = useShareToast(toasterId);

  const handleCardClick = (p: Prompt) => {
    if (swipe.isSwiped) {
      return;
    }
    setActivePrompt(p as WebPrompt);
    setDialogOpen(true);
    onPromptClicked(p);
  };

  const [lastFocusedPromptId, setLastFocusedPromptId] = React.useState<
    string | null
  >(null);

  const [currentFocusedPromptId, setCurrentFocusedPromptId] = React.useState<
    string | null
  >(null);

  const [focusOnNextPrompt, setFocusOnNextPrompt] = React.useState(false);

  const handleDialogClose = React.useCallback(() => {
    setDialogOpen(false);
    if (activePrompt) {
      const promptRef = promptRefs.current.get(activePrompt.Id);
      if (promptRef && promptRef.current) {
        promptRef.current.focus();
      }
    }
    searchParams.delete("promptId");
    setSearchParams(searchParams);
    setCustomerDataParams(customerDataParams);
  }, [activePrompt]);

  function handleCardFocus() {
    if (lastFocusedPromptId) {
      // Set the flag to focus on the next prompt
      setFocusOnNextPrompt(true);
    }
  }

  const [searchParams, setSearchParams] = useSearchParams();
  const [customerDataParams, setCustomerDataParams] = React.useState({});

  function handleFilterChange(
    selectedValues: Record<string, string[]>,
    changedFilters: Filter[]
  ) {
    const selectedFiltersForTelemetry = structuredClone(selectedValues);
    for (const filter of filters) {
      if (!filter.isLogValuesInTelemetry) {
        if (filter.telemetryValueOverrides) {
          const telemetryOverrideList = [];
          // Cross-reference with override list to see what should be logged for telemetry given a provider name
          for (const providerName of selectedFiltersForTelemetry[filter.key]) {
            if (filter.telemetryValueOverrides[providerName]) {
              telemetryOverrideList.push(
                filter.telemetryValueOverrides[providerName]
              );
            }
          }
          selectedFiltersForTelemetry[filter.key] = telemetryOverrideList;
        } else {
          selectedFiltersForTelemetry[filter.key] = [];
        }
      }
    }
    telemetry.sendPromptsFilterEvent(selectedFiltersForTelemetry);
    telemetry.sendFilterActionEvent(
      selectedTab === "GroupPrompts" ? "WorkgroupView" : selectedTab,
      "Filter",
      "ValueApply",
      {
        changedFilters: changedFilters.map((filter: Filter) => filter.key),
      }
    );

    const paramsObj = serializeFilters(
      selectedValues,
      searchParams,
      customerDataParams
    );

    setCustomerDataParams(paramsObj.customerDataParams);
    setSearchParams(paramsObj.searchParams);
  }

  function handleFiltersCleared() {
    telemetry.sendFilterActionEvent(
      selectedTab === "GroupPrompts" ? "WorkgroupView" : selectedTab,
      "Filter",
      "Clear"
    );
    setSearchParams(new URLSearchParams());
    setCustomerDataParams({});
  }

  function handleFiltersOpenChanged(isOpen: boolean, filters: Filter[]) {
    telemetry.sendFilterActionEvent(
      selectedTab === "GroupPrompts" ? "WorkgroupView" : selectedTab,
      "Filter",
      isOpen ? "Open" : "Close",
      {
        filters: filters.map((filter) => filter.key),
      }
    );
  }

  React.useEffect(() => {
    if (filters.length > 0) {
      const selectedFilters = deserializeFilters(
        searchParams,
        filters,
        customerDataParams
      );
      onFilterChanged(selectedFilters);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, customerDataParams, filters]);

  const handleShareViaCopyLinkClicked = React.useCallback(
    async (prompt: Prompt) => {
      try {
        await sharePrompt.shareUrlViaCopyLink(prompt);
        notifyShareCopyLinkSuccess(prompt);
      } catch (_) {
        notifyShareCopyLinkFail();
      }
    },
    [sharePrompt, notifyShareCopyLinkSuccess, notifyShareCopyLinkFail]
  );

  const handleShareViaEmailClicked = React.useCallback(
    async (prompt: Prompt) => {
      notifyShareEmailLinkSuccess(prompt);
      await sharePrompt.shareUrlViaEmail(prompt, instance);
    },
    [sharePrompt, notifyShareEmailLinkSuccess]
  );

  React.useLayoutEffect(() => {
    if (focusOnNextPrompt && lastFocusedPromptId) {
      // Find the index of the last focused prompt
      const lastFocusedPromptIndex = prompts.findIndex(
        (prompt) => prompt.Id === lastFocusedPromptId
      );

      // Check if there's a next prompt in the list
      if (
        lastFocusedPromptIndex !== -1 &&
        lastFocusedPromptIndex + 1 < prompts.length
      ) {
        const nextPrompt = prompts[lastFocusedPromptIndex + 1];
        const promptRef = promptRefs.current.get(nextPrompt.Id);
        if (promptRef && promptRef.current) {
          // Focus on the next prompt
          promptRef.current.focus();
          setLastFocusedPromptId(null);
          setCurrentFocusedPromptId(nextPrompt.Id);
        }
      }

      // Reset the flag
      setFocusOnNextPrompt(false);
    }
  }, [prompts, lastFocusedPromptId, focusOnNextPrompt, promptRefs]);

  const { isFlightActive } = useFlightContext();

  const handleTabChange = React.useCallback(
    (tabToOpen: PromptTabs | null) => {
      if (!onPromptsTabSelected) {
        return;
      }

      if (
        selectedTab === tabToOpen ||
        (selectedTab === "AllPrompts" && tabToOpen === null)
      ) {
        return;
      }

      onPromptsTabSelected(tabToOpen || "AllPrompts");
    },
    [onPromptsTabSelected, selectedTab]
  );

  // Storing initial render to prevent page navigation when toggling bookmark toggle button instead of tab,
  // as bookmark toggle does not change tab query parameter (stays null)
  // leading to immediate change back to AllPrompts tab for toggle button
  const [isInitialRender, setInitialRender] = React.useState(true);
  React.useEffect(() => {
    if (!isInitialRender) {
      return;
    }
    const tabInUrl: PromptTabs | null = searchParams.get(
      "tab"
    ) as PromptTabs | null;
    handleTabChange(tabInUrl);
    setInitialRender(false);
  }, [searchParams, handleTabChange, isInitialRender]);

  let promptsToDisplay = prompts;
  if (selectedTab === "GroupPrompts") {
    promptsToDisplay = groupPrompts;
  }

  const [
    workgroupPublisherTeachingPopoverTarget,
    setworkgroupPublisherTeachingPopoverTarget,
  ] = React.useState<HTMLElement | null>(null);

  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const { isWorkgroupPublishingEnabled } = useFeatureEnablement();
  React.useEffect(() => {
    if (workgroupPublisherTeachingPopoverTarget) {
      // The popover target is hidden by default, so we update it's CSS so that the component can be shown
      if (isPopoverOpen) {
        workgroupPublisherTeachingPopoverTarget.classList.add("popover-open");
      } else {
        workgroupPublisherTeachingPopoverTarget.classList.remove(
          "popover-open"
        );
      }
    }
  }, [isPopoverOpen, workgroupPublisherTeachingPopoverTarget]);

  const handleGroupPromptsFilterOnOpenChange = React.useCallback(
    (isOpen: boolean) => {
      telemetry.sendFilterActionEvent(
        "WorkgroupView",
        "GroupPromptsFilter",
        isOpen ? "Open" : "Close"
      );
    },
    [telemetry]
  );

  /**
   * When the user shares a prompt to a team from a prompt details dialog,
   * we want to first hide the details dialog, and only then display the shareToWorkgroup dialog.
   *
   * If they cancel the operation, the details dialog will be displayed again.
   */
  const [
    dialogHiddenUnderShareToWorkgroup,
    setDialogHiddenUnderShareToWorkgroup,
  ] = React.useState(false);

  const handleShareToWorkgroup = React.useCallback(
    (prompts: Prompt[]) => {
      if (dialogOpen) {
        handleDialogClose();
        setDialogHiddenUnderShareToWorkgroup(true);
      }
      sharePrompt.shareToWorkgroup(prompts);
    },
    [dialogOpen, handleDialogClose, sharePrompt]
  );

  const handleCloseShareToWorkgroupDialog = React.useCallback(
    (isClosedByCustomer: boolean, prompts: Prompt[]) => {
      if (isClosedByCustomer && dialogHiddenUnderShareToWorkgroup) {
        setDialogOpen(true);
      }

      setDialogHiddenUnderShareToWorkgroup(false);
      sharePrompt.onCloseShareToWorkgroupDialog(isClosedByCustomer, prompts);
    },
    [dialogHiddenUnderShareToWorkgroup, sharePrompt]
  );

  const handlePromptChange = React.useCallback(
    (promptToOpen: Prompt) => {
      handleCardClick(promptToOpen);
    },
    [handleCardClick]
  );

  React.useEffect(() => {
    const promptIdInURL = searchParams.get("promptId");
    if (!promptIdInURL) {
      return;
    } else {
      const promptToOpen = allPrompts.find((p) => p.PromptId === promptIdInURL);
      if (!promptToOpen) {
        return;
      }
      handlePromptChange(promptToOpen);
    }
    setInitialRender(false);
  }, [allPrompts]);

  return {
    activePrompt,
    currentFocusedPromptId,
    dialogOpen,
    handleCardClick,
    handleCardFocus,
    handleDialogClose,
    handleFilterChange,
    handleFiltersCleared,
    handleFiltersOpenChanged,
    handleGroupPromptsFilterOnOpenChange,
    handleShareViaCopyLinkClicked,
    handleShareViaEmailClicked,
    handleTabChange,
    isFlightActive,
    isLoading,
    isShareToWorkgroupDialogOpen: sharePrompt.isShareToWorkgroupDialogOpen,
    isWorkgroupPublishingEnabled,
    onCloseShareToWorkgroupDialog: handleCloseShareToWorkgroupDialog,
    promptRefs,
    promptsToDisplay,
    promptsToShareToWorkgroup: sharePrompt.promptsToShareToWorkgroup,
    resultsAnnouncementId,
    setDialogOpen,
    setIsPopoverOpen,
    setLastFocusedPromptId,
    setCurrentFocusedPromptId,
    setworkgroupPublisherTeachingPopoverTarget,
    shareToWorkgroup: handleShareToWorkgroup,
    swipe,
    toasterId,
    workgroupPublisherTeachingPopoverTarget,
    onGroupDropdownOptionSelect: sharePrompt.onGroupDropdownOptionSelect,
    onGroupDropdownOpenChange: sharePrompt.onGroupDropdownOpenChange,
  };
};
